'use client';

import {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  Autocomplete,
  type AutocompleteFieldProps,
  type AutocompleteOption,
} from '@kit/ui/autocomplete';
import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';

import { searchProfilesAction } from '../server/profile-search-actions';
import { getProfileInitials, ProfileRow } from './profile-row';
import type { ProfileSummary } from './profile-types';

export interface ProfilePickerProps extends AutocompleteFieldProps {
  /** Controlled selection: an id (single) or ids (multiple). */
  value?: string | string[];
  /** Multi-select with a removable-chip row. Default false (single). */
  multiple?: boolean;
  /** Fires with the id(s) for form binding. */
  onChange?: (value: string | string[] | null) => void;
  /** Fires with the FULL entity on each add — for side-effects (rate, load). */
  onSelect?: (profile: ProfileSummary) => void;
  /** Fires with the FULL entity on each remove. */
  onDeselect?: (profile: ProfileSummary) => void;
  /**
   * Display data for ids already in `value` at mount (edit mode) so chips /
   * the collapsed single-select display render names + avatars, not raw ids.
   */
  initialSelected?: ProfileSummary[];
  /**
   * Static option set. When provided, the picker skips the async server search
   * and filters this list client-side (preloaded-cast surfaces).
   */
  options?: ProfileSummary[];
  /** Ids to hide from the result list (e.g. profiles already added elsewhere). */
  excludeIds?: string[];
  /**
   * Ids floated to the TOP of the result list, preserving each group's
   * relative order (ranked block first, then the rest). Generic ranking hook
   * — a surface with event context passes e.g. "interested" dancer ids so they
   * surface first, without the canonical search action knowing about events.
   */
  rankIds?: string[];
  /**
   * Extra node rendered at a row's trailing edge, composed with the built-in
   * selected-✓ (e.g. an "interested in this event" star badge). Receives the
   * full profile so the surface decides per row.
   */
  renderTrailing?: (profile: ProfileSummary) => ReactNode;
  /**
   * Overrides/augments a row's secondary line (the default `@handle`) — e.g. a
   * dancer's dance-styles subtitle. Return `undefined`/`null` to keep the
   * default `@handle` line for that row.
   */
  renderSubtitle?: (profile: ProfileSummary) => ReactNode;
  /** Allow creating a profile from the typed text (find-or-create / invite). */
  creatable?: boolean;
  onCreate?: (name: string) => ProfileSummary | Promise<ProfileSummary>;
  /** Optional cap on how many profiles can be selected (multiple only). */
  maxSelections?: number;
  /** Clear the input after a selection (action-picker vs bound field). */
  clearOnSelect?: boolean;
  /** Context slot rendered above the search (e.g. "Add dancers to <roster>"). */
  header?: ReactNode;
  placeholder?: string;
  emptyMessage?: string;
  minChars?: number;
  /** Max server results per search (default 10). */
  searchLimit?: number;
  disabled?: boolean;
  className?: string;
  /** Visible label rendered by the primitive (linked to the input). */
  label?: string;
}

/**
 * THE canonical profile (dancer) picker — single or multi. A thin wrapper over
 * `@kit/ui/autocomplete` (the BAL-347 canonical searchable-select) that renders
 * the shared rich `ProfileRow`, self-fetches via the one canonical
 * `searchProfilesAction`, and lets the primitive own chips + the selection cap
 * (this wrapper renders NO strip of its own — the primitive draws chips in-field
 * on desktop and in a row on mobile). Mobile bottom-sheet, keyboard nav, ARIA
 * are inherited. Mirror of `OrganizationPicker`.
 *
 * Two callback tiers:
 * - `onChange(ids)` — for react-hook-form value binding.
 * - `onSelect(entity)` / `onDeselect(entity)` — hand back the FULL profile so a
 *   parent can run a side-effect (fetch billing details, set a rate) without a
 *   second round-trip.
 */
export const ProfilePicker = forwardRef<HTMLDivElement, ProfilePickerProps>(
  (
    {
      value,
      multiple = false,
      onChange,
      onSelect,
      onDeselect,
      initialSelected,
      options: staticOptions,
      excludeIds,
      rankIds,
      renderTrailing,
      renderSubtitle,
      creatable,
      onCreate,
      maxSelections,
      clearOnSelect,
      header,
      placeholder,
      emptyMessage,
      minChars = 2,
      searchLimit,
      disabled,
      className,
      label,
      'aria-label': ariaLabel,
      ...fieldProps
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const isStatic = Array.isArray(staticOptions);

    const [results, setResults] = useState<ProfileSummary[]>(
      staticOptions ?? [],
    );
    const [isFetching, setIsFetching] = useState(false);
    const [searchTooShort, setSearchTooShort] = useState(true);

    // Guards against out-of-order async responses (last request wins).
    const requestIdRef = useRef(0);

    // Cache hits keyed by id so selected chips / the collapsed single display
    // keep resolving after the option list changes. Seeded from
    // `initialSelected` + any static options.
    const [cache, setCache] = useState<Map<string, ProfileSummary>>(
      () =>
        new Map(
          [...(initialSelected ?? []), ...(staticOptions ?? [])].map((p) => [
            p.id,
            p,
          ]),
        ),
    );

    // Synchronous mirror of `cache`. `setCache` commits asynchronously, but
    // `onCreate` caches the freshly-minted profile and the primitive fires
    // `onSelect(resolve(newId))` in the SAME tick — so `resolve` must see the
    // new entry before React re-renders. Reading the ref closes that race;
    // without it `resolve` falls through to `{ displayName: id }` and the
    // parent persists the profile UUID as the display name. Same fix as
    // organization-picker.tsx (99da06f178, BAL org-name-as-uuid bug); the
    // create-then-select path here is identical.
    const cacheRef = useRef(cache);

    const cacheProfiles = useCallback((profiles: ProfileSummary[]) => {
      if (profiles.length === 0) return;
      const next = new Map(cacheRef.current);
      for (const p of profiles) next.set(p.id, p);
      cacheRef.current = next; // sync: visible to a same-tick resolve()
      setCache(next); // async: re-render so chips/labels re-resolve
    }, []);

    const resolve = useCallback(
      (id: string): ProfileSummary =>
        cache.get(id) ??
        cacheRef.current.get(id) ??
        results.find((r) => r.id === id) ?? {
          id,
          displayName: id,
          avatarUrl: null,
        },
      [cache, results],
    );

    const selectedIds = useMemo<string[]>(
      () => (Array.isArray(value) ? value : value ? [value] : []),
      [value],
    );
    const prevIdsRef = useRef<string[]>(selectedIds);

    const handleInputChange = useCallback(
      async (query: string) => {
        if (isStatic) return; // client-side filter over static options
        const trimmed = query.trim();
        setSearchTooShort(trimmed.length < minChars);
        if (trimmed.length < minChars) {
          setResults([]);
          return;
        }
        const reqId = ++requestIdRef.current;
        setIsFetching(true);
        try {
          const result = await searchProfilesAction(trimmed, {
            limit: searchLimit,
          });
          if (reqId !== requestIdRef.current) return; // stale response
          const hits = result.success ? result.data : [];
          setResults(hits);
          cacheProfiles(hits);
        } finally {
          if (reqId === requestIdRef.current) setIsFetching(false);
        }
      },
      [cacheProfiles, isStatic, minChars, searchLimit],
    );

    const toOption = useCallback(
      (p: ProfileSummary): AutocompleteOption => ({
        value: p.id,
        label: p.displayName,
        description: p.handle ? `@${p.handle}` : undefined,
        icon: (
          <Avatar className="h-5 w-5">
            <AvatarImage src={p.avatarUrl ?? undefined} />
            <AvatarFallback className="text-[10px]">
              {getProfileInitials(p.displayName)}
            </AvatarFallback>
          </Avatar>
        ),
      }),
      [],
    );

    // Options = current result set PLUS any selected-but-not-in-results entries,
    // so the primitive can resolve chip labels/avatars for ids the current
    // search no longer returns (edit mode, post-query-change).
    const options = useMemo<AutocompleteOption[]>(() => {
      const excluded = new Set(excludeIds ?? []);
      const source = (isStatic ? (staticOptions ?? []) : results).filter(
        (p) => !excluded.has(p.id),
      );
      const byId = new Map(source.map((p) => [p.id, p]));
      // Selected ids always resolve (so their chips render) even if excluded.
      for (const id of selectedIds) {
        if (!byId.has(id)) byId.set(id, resolve(id));
      }
      let ordered = [...byId.values()];
      // Float ranked ids to the top via a STABLE partition (relative order
      // within the ranked block and the rest is preserved).
      const rankSet = new Set(rankIds ?? []);
      if (rankSet.size > 0) {
        ordered = [
          ...ordered.filter((p) => rankSet.has(p.id)),
          ...ordered.filter((p) => !rankSet.has(p.id)),
        ];
      }
      return ordered.map(toOption);
    }, [
      excludeIds,
      isStatic,
      results,
      staticOptions,
      selectedIds,
      rankIds,
      resolve,
      toOption,
    ]);

    const fireDiff = useCallback(
      (nextIds: string[]) => {
        const prev = prevIdsRef.current;
        const prevSet = new Set(prev);
        const nextSet = new Set(nextIds);
        for (const id of nextIds) if (!prevSet.has(id)) onSelect?.(resolve(id));
        for (const id of prev) if (!nextSet.has(id)) onDeselect?.(resolve(id));
        prevIdsRef.current = nextIds;
      },
      [onDeselect, onSelect, resolve],
    );

    const handleChange = useCallback(
      (next: string | string[] | null) => {
        if (multiple) {
          const ids = Array.isArray(next) ? next : next ? [next] : [];
          fireDiff(ids);
          onChange?.(ids);
        } else {
          const id = Array.isArray(next) ? (next[0] ?? null) : next;
          fireDiff(id ? [id] : []);
          onChange?.(id);
        }
      },
      [fireDiff, multiple, onChange],
    );

    const handleCreate = useMemo(() => {
      if (!creatable || !onCreate) return undefined;
      return async (inputValue: string): Promise<AutocompleteOption> => {
        const created = await onCreate(inputValue);
        cacheProfiles([created]);
        return toOption(created);
      };
    }, [cacheProfiles, creatable, onCreate, toOption]);

    const renderOption = useCallback(
      (option: AutocompleteOption, isSelected: boolean) => {
        const profile = resolve(option.value);
        const extraTrailing = renderTrailing?.(profile);
        const subtitle = renderSubtitle?.(profile);
        const check = isSelected ? (
          <span className="text-primary text-xs">✓</span>
        ) : null;
        return (
          <ProfileRow
            profile={profile}
            subtitle={subtitle ?? undefined}
            trailing={
              extraTrailing || check ? (
                <span className="flex items-center gap-1.5">
                  {extraTrailing}
                  {check}
                </span>
              ) : undefined
            }
          />
        );
      },
      [resolve, renderTrailing, renderSubtitle],
    );

    const resolvedEmpty =
      emptyMessage ??
      (!isStatic && searchTooShort
        ? t('common:typeToSearch', 'Type to search…')
        : t('common:noResults', 'No results'));

    return (
      <div ref={ref} className={className}>
        <Autocomplete
          {...fieldProps}
          label={label}
          multiple={multiple}
          maxSelections={maxSelections}
          header={header}
          clearOnSelect={clearOnSelect ?? multiple}
          serverFiltered={!isStatic}
          value={multiple ? selectedIds : (selectedIds[0] ?? undefined)}
          onChange={handleChange}
          options={options}
          loading={isFetching}
          disabled={disabled}
          onInputChange={isStatic ? undefined : handleInputChange}
          renderOption={renderOption}
          creatable={creatable}
          onCreate={handleCreate}
          minChars={minChars}
          placeholder={placeholder ?? t('common:searchProfiles', 'Search…')}
          emptyMessage={resolvedEmpty}
          aria-label={ariaLabel}
        />
      </div>
    );
  },
);

ProfilePicker.displayName = 'ProfilePicker';
